import { useMemo } from 'react';
import { Table, Pagination, Empty, Spin, Grid } from 'antd';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import type { Breakpoint } from 'antd';

const { useBreakpoint } = Grid;

/**
 * ResponsiveTableOrCards
 * ----------------------------------------------------------------------------
 * Renders an Ant Design Table at wider viewports and a list of cards at narrow
 * viewports. The cutoff is controlled by the `breakpoint` prop (defaults to
 * 'lg', i.e., switches to cards below 992px).
 *
 * PAGINATION MODES:
 *   - Client-side (default): wrapper slices dataSource for the card view.
 *     Use for pages that load all data upfront (Users, Assets, Orgs).
 *   - Server-side (serverSidePagination=true): wrapper renders all rows in
 *     dataSource as-is. The caller's pagination.onChange fires a new server
 *     fetch. Use for pages with large datasets (AuditLog, SyncHistory).
 */

export type SelectionHelpers = {
  selected: boolean;
  selectDisabled: boolean;
  onSelectChange: (selected: boolean) => void;
};

type RowSelectionConfig<T> = {
  selectedRowKeys: React.Key[];
  onChange: (keys: React.Key[], rows: T[]) => void;
  getCheckboxProps?: (record: T) => { disabled?: boolean };
};

type Props<T> = {
  dataSource: T[];
  rowKey: (record: T) => React.Key;
  loading?: boolean;

  /** Columns for the Table view (used at wide viewports) */
  columns: TableColumnsType<T>;

  /** Renderer for the card view (used at narrow viewports) */
  renderCard: (record: T, helpers: SelectionHelpers) => React.ReactNode;

  /** Pagination config (or false to disable). Shared between modes. */
  pagination?: TablePaginationConfig | false;

  /**
   * When true, the wrapper does NOT slice dataSource for the card view.
   * The caller is responsible for fetching the correct page of data.
   * The Pagination component uses pagination.total for the total count.
   * Use for AuditLog, SyncHistory, and any other server-paginated page.
   */
  serverSidePagination?: boolean;

  /** Selection config. When provided, both views support selection. */
  rowSelection?: RowSelectionConfig<T>;

  /**
   * Below this Ant Design breakpoint, the card view is rendered. Defaults to
   * 'lg' (i.e. cards below 992px, table at 992px+).
   */
  breakpoint?: Breakpoint;

  /** Empty state message (defaults to Ant's default) */
  emptyText?: React.ReactNode;
};

function ResponsiveTableOrCards<T extends object>({
  dataSource,
  rowKey,
  loading = false,
  columns,
  renderCard,
  pagination,
  serverSidePagination = false,
  rowSelection,
  breakpoint = 'lg',
  emptyText,
}: Props<T>) {
  const screens = useBreakpoint();
  const useTableView = !!screens[breakpoint];

  const paginationConfig: TablePaginationConfig | null = useMemo(() => {
    if (pagination === false || !pagination) return null;
    return { current: 1, pageSize: 20, ...pagination };
  }, [pagination]);

  // ------------------------------------------------------------------
  // TABLE VIEW (wide viewports)
  // ------------------------------------------------------------------
  if (useTableView) {
    return (
      <Table<T>
        dataSource={dataSource}
        rowKey={rowKey}
        columns={columns}
        loading={loading}
        pagination={pagination}
        scroll={{ x: 'max-content' }}
        rowSelection={
          rowSelection
            ? {
                selectedRowKeys: rowSelection.selectedRowKeys,
                onChange: rowSelection.onChange,
                getCheckboxProps: rowSelection.getCheckboxProps,
              }
            : undefined
        }
        locale={emptyText ? { emptyText } : undefined}
      />
    );
  }

  // ------------------------------------------------------------------
  // CARD VIEW (narrow viewports)
  // ------------------------------------------------------------------
  const selectedSet = new Set(rowSelection?.selectedRowKeys ?? []);

  // Client-side mode: slice dataSource to the current page.
  // Server-side mode: render all rows as-is (caller already fetched the page).
  let pagedData = dataSource;
  if (!serverSidePagination && paginationConfig) {
    const current = paginationConfig.current ?? 1;
    const pageSize = paginationConfig.pageSize ?? 20;
    const start = (current - 1) * pageSize;
    pagedData = dataSource.slice(start, start + pageSize);
  }

  // Total count for the Pagination component.
  // Server-side: use pagination.total (the full dataset count from the server).
  // Client-side: use dataSource.length (full local array).
  const paginationTotal = serverSidePagination
    ? (paginationConfig?.total ?? dataSource.length)
    : dataSource.length;

  const handleSelectChange = (record: T, selected: boolean) => {
    if (!rowSelection) return;
    const key = rowKey(record);
    const newKeys = selected
      ? [...rowSelection.selectedRowKeys, key]
      : rowSelection.selectedRowKeys.filter(k => k !== key);
    const newRows = dataSource.filter(r => newKeys.includes(rowKey(r)));
    rowSelection.onChange(newKeys, newRows);
  };

  return (
    <Spin spinning={loading}>
      {pagedData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={emptyText ?? 'No data'}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <div>
          {pagedData.map(record => {
            const key = rowKey(record);
            const checkboxProps = rowSelection?.getCheckboxProps?.(record);
            const helpers: SelectionHelpers = {
              selected: selectedSet.has(key),
              selectDisabled: !!checkboxProps?.disabled,
              onSelectChange: (sel: boolean) => handleSelectChange(record, sel),
            };
            return <div key={String(key)}>{renderCard(record, helpers)}</div>;
          })}
        </div>
      )}

      {paginationConfig && paginationTotal > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination {...paginationConfig} total={paginationTotal} simple />
        </div>
      )}
    </Spin>
  );
}

export default ResponsiveTableOrCards;
